/**
 * Chunked resumable upload client (TUS-like subset) — #477.
 *
 * Ports the vanilla-JS logic from `backend/app/video/static/upload.html`
 * into a typed module consumed by the multi-file upload queue on the home
 * page (`(app)/page.tsx`) — #477 D1/D2: criação de levantamento + upload de
 * N vídeos NA MESMA TELA, sem passar pela página de upload standalone.
 *
 * Protocolo (backend/app/video/upload_router.py):
 *
 *   POST /upload/init             {filename, size_bytes}      -> {upload_id, offset}
 *   GET  /upload/{id}                                          -> {offset, size_bytes, filename}
 *   PUT  /upload/{id}/chunk        header Upload-Offset + corpo cru -> {offset}
 *                                   409 (detail={offset}) se dessincronizado -- resync + retry
 *   POST /upload/{id}/complete     {levantamento_id, process}  -> {video_id, status, warning}
 *
 * `process` é SEMPRE `false` neste cliente (#477 D2 — o backend já suporta
 * criação/upload/process como 3 passos distintos; "Processar" continua um
 * botão explícito separado, nunca disparado por este módulo).
 *
 * Simplificação deliberada vs. upload.html: sem retomada via localStorage
 * entre reloads de página — o upload vive dentro do modal React da mesma
 * sessão; um refresh de página no meio do upload perde o progresso (aceito,
 * fora do escopo #477/#483).
 */

import { api } from "@/lib/api";
import { getNkToken } from "@/lib/auth-token";

const API_URL =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001/api/v1";

/** Espelha o tamanho de pedaço do backend (upload_router.py). */
export const UPLOAD_CHUNK_SIZE = 8 * 1024 * 1024; // 8MB

const MAX_RETRIES = 6;
/** Limite de re-sincronizações de offset (409) por chunk -- evita loop sem
 * fim se o mesmo upload_id estiver sendo escrito por outra aba/sessão. */
const MAX_RESYNC_ATTEMPTS = 30;

export interface UploadInitOut {
  upload_id: string;
  offset: number;
}

export interface UploadProbeOut {
  offset: number;
  size_bytes: number;
  filename: string;
}

export interface UploadCompleteOut {
  video_id: string;
  status: string;
  warning: string | null;
}

export const initUpload = (
  filename: string,
  sizeBytes: number,
): Promise<UploadInitOut> =>
  api.post<UploadInitOut>("/upload/init", {
    filename,
    size_bytes: sizeBytes,
  });

export const probeUpload = (uploadId: string): Promise<UploadProbeOut> =>
  api.get<UploadProbeOut>(`/upload/${uploadId}`);

export const completeUpload = (
  uploadId: string,
  levantamentoId: string,
  process = false,
): Promise<UploadCompleteOut> =>
  api.post<UploadCompleteOut>(`/upload/${uploadId}/complete`, {
    levantamento_id: levantamentoId,
    process,
  });

/** Erros permanentes do cliente -- credencial ruim, upload sumiu, chunk
 * grande demais, corpo/estado inválido. Retry com backoff não resolve
 * nenhum desses; só falha transitória de rede/5xx merece backoff. */
function isPermanentStatus(status: number): boolean {
  return status === 401 || status === 404 || status === 413 || status === 422;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * PUT /upload/{id}/chunk cru -- não passa por `api.put` (que força JSON):
 * aqui o corpo é binário (Blob) com header `Upload-Offset`. Resync em 409,
 * backoff exponencial em falha transitória -- mesma lógica de upload.html.
 */
async function putChunkWithRetry(
  uploadId: string,
  file: File,
  startOffset: number,
): Promise<number> {
  let attempt = 0;
  let resyncAttempts = 0;
  let currentOffset = startOffset;

  async function attemptOnce(): Promise<number> {
    const slice = file.slice(currentOffset, currentOffset + UPLOAD_CHUNK_SIZE);
    const token = getNkToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/octet-stream",
      "Upload-Offset": String(currentOffset),
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    let res: Response;
    try {
      res = await fetch(`${API_URL}/upload/${uploadId}/chunk`, {
        method: "PUT",
        headers,
        body: slice,
      });
    } catch (err) {
      return retryAfterTransientFailure(err);
    }

    if (res.status === 409) {
      resyncAttempts += 1;
      if (resyncAttempts > MAX_RESYNC_ATTEMPTS) {
        throw new Error(
          "Muitas re-sincronizações de offset -- upload provavelmente sendo " +
            "escrito por outra aba/sessão ao mesmo tempo.",
        );
      }
      const body = await res.json().catch(() => null);
      const serverOffset =
        body && body.detail && typeof body.detail.offset === "number"
          ? (body.detail.offset as number)
          : (await probeUpload(uploadId)).offset;
      currentOffset = serverOffset;
      return attemptOnce();
    }

    if (isPermanentStatus(res.status)) {
      const detail = await res.text();
      const err = new Error(`chunk falhou (${res.status}): ${detail}`);
      throw err;
    }

    if (!res.ok) {
      return retryAfterTransientFailure(
        new Error(`chunk falhou (${res.status})`),
      );
    }

    const data = await res.json();
    return data.offset as number;
  }

  async function retryAfterTransientFailure(err: unknown): Promise<number> {
    attempt += 1;
    if (attempt > MAX_RETRIES) throw err;
    await sleep(Math.min(1000 * 2 ** attempt, 16_000));
    try {
      currentOffset = (await probeUpload(uploadId)).offset;
    } catch {
      // Probe falhou também — mantém o offset conhecido; a próxima tentativa
      // reusa. Se o backend seguir fora do ar, MAX_RETRIES eventualmente
      // desiste e propaga o erro pro caller.
    }
    return attemptOnce();
  }

  return attemptOnce();
}

export interface UploadVideoFileOptions {
  onProgress?: (pct: number) => void;
}

/**
 * Orquestra o upload completo de 1 arquivo: init -> loop de chunks (retry +
 * resync) -> complete com `process: false`. Usado pela fila sequencial
 * client-side do modal de criação (#477 achado 2: "fila sequencial no
 * cliente, infra chunked já existe").
 */
export async function uploadVideoFile(
  file: File,
  levantamentoId: string,
  opts: UploadVideoFileOptions = {},
): Promise<UploadCompleteOut> {
  const init = await initUpload(file.name, file.size);
  let offset = init.offset;

  while (offset < file.size) {
    offset = await putChunkWithRetry(init.upload_id, file, offset);
    const pct = file.size === 0 ? 100 : Math.round((offset / file.size) * 100);
    opts.onProgress?.(pct);
  }

  opts.onProgress?.(100);
  return completeUpload(init.upload_id, levantamentoId, false);
}
